import React from 'react';

import { Card } from '@apigrowfinance/pbolt-card';
import { InactiveUser } from '@apigrowfinance/pbolt-icons';
import IllustratedMessage from '@apigrowfinance/pbolt-illustrated-message';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PhoneDetails } from '@clients/accountsClient';
import { CustomersItem } from '@clients/customersClient';
import IllustratedMessageLoadingError from '@components/LoadingErrorMessage/LoadingErrorMessage';
import { useAccount } from '@contexts/AccountProvider';
import useGetCustomerContactDetails from '@hooks/queries/useGetCustomerContactDetails';
import useGetCustomers from '@hooks/queries/useGetCustomers';
import CustomerPhonesRowContainer from '@pages/accountDetail/tabs/phoneNumbers/CustomerPhonesRowContainer';

type CustomerPhoneNumber = CustomersItem & {
    phoneNumbers?: PhoneDetails[];
};

const CustomersPhonesContainer = () => {
    const { accountId } = useParams();
    const { isLoading: isLoadingAccounts } = useAccount();

    // get customer on an account
    const {
        data,
        isLoading: isLoadingCustomers,
        isError,
        refetch: refetchCustomers,
    } = useGetCustomers(parseFloat(accountId));

    // get phones on an account
    const {
        phoneData,
        phoneIsLoading,
        phoneIsError,
        refetch: refetchCustomerContact,
    } = useGetCustomerContactDetails(parseFloat(accountId));

    const refetch = () => {
        refetchCustomers();
        refetchCustomerContact();
    };

    const isLoadingAnything =
        isLoadingAccounts || isLoadingCustomers || phoneIsLoading;

    // sort into customers and filter by active numbers
    const phoneDataDetails =
        data?.items && !phoneIsLoading
            ? data?.items?.map((customerItem) => {
                  const newCustomers: CustomerPhoneNumber = customerItem;
                  newCustomers.phoneNumbers = phoneData?.filter(
                      (phoneNumber) =>
                          phoneNumber.customer_id ===
                              customerItem.customer.id && phoneNumber.active,
                  );
                  return newCustomers;
              })
            : [];

    if (isError || phoneIsError) {
        return (
            <Card isTopRadiusBorder={false}>
                <IllustratedMessageLoadingError />
            </Card>
        );
    }

    if (!isLoadingAnything && phoneDataDetails.length == 0) {
        return (
            <Card isTopRadiusBorder={false}>
                <IllustratedMessage
                    icon={<InactiveUser color="inherit" />}
                    messageMain={
                        <FormattedMessage
                            id="Phones.noPhonesToShow"
                            defaultMessage="No phones to show"
                        />
                    }
                    messageSub={
                        <FormattedMessage
                            id="Phones.thereHaveBeenNoPhonesAdded"
                            defaultMessage="There have been no phones added"
                        />
                    }
                />
            </Card>
        );
    }

    if (isLoadingAnything && phoneDataDetails?.length == 0) {
        // if loading and no data, then create some dummy data to be
        phoneDataDetails.push({
            account_id: 103134216,
            customer: {
                id: 103132425,
                printed_name: 'Jonathan Banana',
                social_name: null,
                nickname: 'Jonathan',
                is_owner: true,
                is_active: true,
                email: '',
            },
            phoneNumbers: [
                {
                    account_id: 103134216,
                    number: '51234',
                    area_code: 55,
                    extension: '',
                    country_code: 55,
                    customer_id: 103132425,
                    active: true,
                    id: 12360273,
                    type: 'MOBILE',
                    org_id: 'TN-e3adf015-986e-401f-ac0f-d9a4b8a55da5',
                },
            ],
        });
    }

    return (
        <>
            {phoneDataDetails?.map(({ customer, phoneNumbers }, index) => (
                <CustomerPhonesRowContainer
                    key={index}
                    account_id={+accountId}
                    customer={customer}
                    isLoading={isLoadingAnything}
                    onRefetch={refetch}
                    phoneNumbers={phoneNumbers}
                />
            ))}
        </>
    );
};

export default CustomersPhonesContainer;
